import { useState } from "react";
import { useNavigate } from "react-router-dom";
import "./PlanSelectionModal.css";

const PLAN_ORDER = ["monthly", "advanced"];

const PLANS = [
  {
    key: "monthly",
    name: "Basic",
    price: 10,
    features: [
      { text: "Unlimited searches per month", state: "included" },
      { text: "Up to 300 abstracts", state: "included" },
      { text: "Upload supporting research", state: "included" },
      { text: "Access to private data", state: "included" },
      { text: "Invite colleagues to join", state: "included" },
      { text: "Access literature review tools", state: "excluded", link: { label: "Contact us", href: "https://www.medrpt.ai/contact-us" } },
    ],
  },
  {
    key: "advanced",
    name: "Advanced",
    price: 25,
    badge: "Recommended",
    features: [
      { text: "Unlimited searches per month", state: "included" },
      { text: "No limit on abstracts", state: "included" },
      { text: "Upload supporting research", state: "included" },
      { text: "Access to private data", state: "included" },
      { text: "Invite colleagues to join", state: "included" },
      { text: "Access literature review tools", state: "excluded", link: { label: "Contact us", href: "https://www.medrpt.ai/contact-us" } },
    ],
  },
];

const PlanFeatureIcon = ({ state }) => {
  if (state === "excluded") {
    return (
      <span className="plan-feature-icon plan-feature-icon--excluded" aria-hidden="true">
        <svg viewBox="0 0 20 20" fill="none" xmlns="http://www.w3.org/2000/svg">
          <circle cx="10" cy="10" r="9" fill="#d1d5db" stroke="#d1d5db" strokeWidth="1" />
          <path
            d="M6 10.5L8.5 13L14 7.5"
            stroke="#fff"
            strokeWidth="1.75"
            strokeLinecap="round"
            strokeLinejoin="round"
          />
        </svg>
      </span>
    );
  }

  if (state === "outline") {
    return (
      <span className="plan-feature-icon plan-feature-icon--outline" aria-hidden="true">
        <svg viewBox="0 0 20 20" fill="none" xmlns="http://www.w3.org/2000/svg">
          <circle cx="10" cy="10" r="8.5" stroke="currentColor" strokeWidth="2" />
        </svg>
      </span>
    );
  }

  return (
    <span className="plan-feature-icon plan-feature-icon--included" aria-hidden="true">
      <svg viewBox="0 0 20 20" fill="none" xmlns="http://www.w3.org/2000/svg">
        <circle cx="10" cy="10" r="9" fill="currentColor" />
        <path
          d="M6 10.5L8.5 13L14 7.5"
          stroke="#fff"
          strokeWidth="1.75"
          strokeLinecap="round"
          strokeLinejoin="round"
        />
      </svg>
    </span>
  );
};

const PlanSelectionModal = ({ onClose, recommendedPlan = "advanced", requiredPlan = null, currentPlan = null, onChangePlan = null }) => {
  const navigate = useNavigate();
  const [switching, setSwitching] = useState(null);

  const requiredIndex = requiredPlan ? PLAN_ORDER.indexOf(requiredPlan) : -1;

  const handleSelect = async (planKey) => {
    if (currentPlan && onChangePlan) {
      setSwitching(planKey);
      try {
        await onChangePlan(planKey);
        onClose();
      } finally {
        setSwitching(null);
      }
    } else {
      onClose();
      navigate(`/checkout?plan=${planKey}`);
    }
  };

  const formatPrice = (amount) =>
    amount.toLocaleString("en-US", {
      style: "currency",
      currency: "USD",
      minimumFractionDigits: 0,
      maximumFractionDigits: 0,
    });

  return (
    <div className="plan-modal-overlay" onClick={onClose}>
      <div className="plan-modal-content" onClick={(e) => e.stopPropagation()}>
        <div className="plan-modal-header">
          <h3>Choose a Plan</h3>
          <button type="button" className="plan-modal-close" onClick={onClose}>
            ×
          </button>
        </div>
        <div className="plan-modal-cards">
          {PLANS.map((plan) => {
            const isRecommended = plan.key === recommendedPlan;
            const isDisabled = requiredIndex > -1 && PLAN_ORDER.indexOf(plan.key) < requiredIndex;
            const isCurrent = plan.key === currentPlan;
            const isSwitching = switching === plan.key;
            return (
              <div
                key={plan.key}
                className={`plan-card${isRecommended ? " plan-card--recommended" : ""}${isDisabled ? " plan-card--disabled" : ""}${isCurrent ? " plan-card--current" : ""}`}
              >
                {isCurrent && (
                  <div className="plan-card-badge plan-card-badge--current">Current Plan</div>
                )}
                {!isCurrent && isRecommended && plan.badge && (
                  <div className="plan-card-badge">{plan.badge}</div>
                )}
                <h4 className="plan-card-name">{plan.name} plan</h4>
                <div className="plan-card-price">
                  <span className="plan-card-price-amount">{formatPrice(plan.price)}</span>
                  <span className="plan-card-price-period">USD per month</span>
                </div>
                <ul className="plan-card-features">
                  {plan.features.map((feature) => (
                    <li
                      key={feature.text}
                      className={`plan-card-features-item plan-card-features-item--${feature.state}`}
                    >
                      <PlanFeatureIcon state={feature.state} />
                      <span className="plan-card-features-text">
                        {feature.text}
                        {feature.link && (
                          <> (<a href={feature.link.href} target="_blank" rel="noopener noreferrer" className="plan-feature-contact-link">{feature.link.label}</a>)</>
                        )}
                      </span>
                    </li>
                  ))}
                </ul>
                <button
                  type="button"
                  className="plan-card-btn"
                  onClick={() => handleSelect(plan.key)}
                  disabled={isDisabled || isCurrent || isSwitching}
                >
                  {isSwitching ? "Switching..." : isCurrent ? "Current Plan" : currentPlan ? "Switch to this plan" : "Select"}
                </button>
                {isDisabled && (
                  <p className="plan-card-unavailable">Doesn't include Literature Review Tools</p>
                )}
              </div>
            );
          })}
        </div>
      </div>
    </div>
  );
};

export default PlanSelectionModal;
